import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function Etudiants() {
  const [etudiants, setEtudiants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEtudiant, setSelectedEtudiant] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [stats, setStats] = useState({ total: 0, valides: 0, invalides: 0, sans_carte: 0 });
  const [evolution, setEvolution] = useState([]);

  // Configuration centralisée
  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');

  const fetchEtudiants = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/admin/etudiants/lister_etudiants.php`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();

      if (data.success) {
        // Normaliser les URLs des photos de profil et des cartes d'identité
        const etudiantsNormalises = (data.etudiants || []).map(etu => ({
          ...etu,
          photo_profil_url: config.normalizeProfilePhoto(etu.photo_profil_url),
          carte_identite_url: config.normalizeDocument(etu.carte_identite_url)
        }));
        setEtudiants(etudiantsNormalises);
      } else if (data.message === 'Session expirée') {
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(data.message || 'Erreur de chargement');
      }
    } catch (error) {
      console.error('Erreur fetch:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/etudiants/stats_validite.php`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats || { total: 0, valides: 0, invalides: 0, sans_carte: 0 });
        setEvolution(data.evolution || []);
      }
    } catch (error) {
      console.error('Erreur stats:', error);
    }
  };

  const changerStatutValidation = async (id, nouveauStatut, nom, prenom) => {
    const actionTexte = nouveauStatut === 'valide' ? 'VALIDER' : 'INVALIDER';
    const confirmation = confirm(`Êtes-vous sûr de vouloir ${actionTexte} la carte de ${prenom} ${nom} ?`);
    if (!confirmation) return;

    try {
      const response = await fetch(`${API_BASE}/admin/etudiants/changer_statut_validation.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          id_etudiant: id,
          statut_validation: nouveauStatut,
        }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchEtudiants();
        fetchStats();
        setShowDetailsModal(false);
      } else {
        toast.error(data.message || "Erreur lors de l'opération");
      }
    } catch (error) {
      toast.error("Erreur lors de l'opération");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatMoney = (value) => {
    return Number(value || 0).toLocaleString('fr-FR') + ' F';
  };

  useEffect(() => {
    fetchEtudiants();
    fetchStats();
  }, []);

  const EvolutionGraph = () => {
    if (!evolution || evolution.length === 0) return null;
    const maxNombre = Math.max(...evolution.map((e) => parseInt(e.nombre, 10) || 0));
    if (maxNombre === 0) return null;

    return (
      <div className="mb-6 rounded-lg bg-white p-4 shadow">
        <h3 className="mb-3 text-lg font-semibold text-gray-800">Évolution des inscriptions</h3>
        <div className="flex items-end gap-2 overflow-x-auto pb-2">
          {evolution.map((item, idx) => (
            <div key={idx} className="flex min-w-[60px] flex-col items-center">
              <div
                className="w-full rounded-t bg-gradient-to-t from-blue-500 to-blue-600 transition hover:from-blue-600 hover:to-blue-700"
                style={{
                  height: `${(parseInt(item.nombre, 10) / maxNombre) * 100}px`,
                  minHeight: '20px',
                }}
              />
              <span className="mt-2 text-xs font-medium text-gray-600">{item.mois}</span>
              <span className="text-xs text-gray-500">{item.nombre} étudiants</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Chargement des étudiants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestion des étudiants</h1>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-blue-50 p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.total || 0}</div>
            <div className="text-xs text-gray-600">Total étudiants</div>
          </div>
          <div className="rounded-lg bg-green-50 p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.valides || 0}</div>
            <div className="text-xs text-gray-600">Cartes validées</div>
          </div>
          <div className="rounded-lg bg-red-50 p-3 text-center">
            <div className="text-2xl font-bold text-red-600">{stats.invalides || 0}</div>
            <div className="text-xs text-gray-600">Cartes invalidées</div>
          </div>
          <div className="rounded-lg bg-gray-50 p-3 text-center">
            <div className="text-2xl font-bold text-gray-600">{stats.sans_carte || 0}</div>
            <div className="text-xs text-gray-600">Sans carte</div>
          </div>
        </div>
      </div>

      <EvolutionGraph />

      <div className="overflow-x-auto rounded-lg bg-white shadow">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Étudiant</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Institution</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Solde CROU</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Statut carte</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {etudiants.map((e) => {
              const aUneCarte = e.id_carte !== null;
              const estValide = e.statut_validation === 'valide';
              return (
                <tr key={e.id_utilisateur} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {e.photo_profil_url ? (
                        <img
                          src={e.photo_profil_url}
                          alt="profil"
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-300 text-sm">📷</div>
                      )}
                      <div>
                        <div className="font-medium">{e.prenom} {e.nom}</div>
                        <div className="text-xs text-gray-500">
                          {e.est_boursier ? 'Boursier' : 'Non boursier'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div>{e.email}</div>
                    <div className="text-xs text-gray-500">{e.telephone || 'Pas de téléphone'}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">{e.institution || '-'}</td>
                  <td className="px-4 py-3 font-semibold text-blue-600">{formatMoney(e.compte_crou)}</td>
                  <td className="px-4 py-3">
                    {!aUneCarte ? (
                      <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">Sans carte</span>
                    ) : estValide ? (
                      <span className="inline-flex rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">Validée</span>
                    ) : (
                      <span className="inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-800">Invalidée</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => {
                        setSelectedEtudiant(e);
                        setShowDetailsModal(true);
                      }}
                      className="rounded bg-blue-500 px-3 py-1 text-xs text-white transition hover:bg-blue-600"
                    >
                      Détails
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {etudiants.length === 0 && (
          <div className="p-8 text-center text-gray-500">Aucun étudiant trouvé</div>
        )}
      </div>

      {showDetailsModal && selectedEtudiant && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowDetailsModal(false)}
        >
          <div
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between border-b pb-3">
              <h2 className="text-xl font-bold text-gray-800">Détails de l'étudiant</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="rounded bg-gray-200 px-3 py-1 transition hover:bg-gray-300"
              >
                X
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {selectedEtudiant.photo_profil_url ? (
                  <img
                    src={selectedEtudiant.photo_profil_url}
                    alt="profil"
                    className="h-20 w-20 rounded-full border-2 border-gray-300 object-cover"
                  />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-300 text-3xl">📷</div>
                )}
                <div>
                  <h3 className="text-lg font-semibold">{selectedEtudiant.prenom} {selectedEtudiant.nom}</h3>
                  <p className="text-gray-600">{selectedEtudiant.est_boursier ? 'Boursier' : 'Non boursier'}</p>
                  <p className="text-sm text-gray-500">Inscrit le : {formatDate(selectedEtudiant.date_inscription)}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 rounded-lg bg-gray-50 p-3 md:grid-cols-2">
                <div><span className="font-semibold">Email :</span> {selectedEtudiant.email}</div>
                <div><span className="font-semibold">Téléphone :</span> {selectedEtudiant.telephone || '-'}</div>
              </div>
              <div className="rounded-lg border p-3">
                <h4 className="mb-2 font-semibold text-blue-600">Informations de la carte</h4>
                {selectedEtudiant.id_carte ? (
                  <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                    <div><span className="font-semibold">Institution :</span> {selectedEtudiant.institution || '-'}</div>
                    <div><span className="font-semibold">VDDNN :</span> {selectedEtudiant.vddnn || '-'}</div>
                    <div><span className="font-semibold">GDDNN :</span> {selectedEtudiant.gddnn || '-'}</div>
                    <div><span className="font-semibold">Solde CROU :</span> <span className="font-bold text-blue-600">{formatMoney(selectedEtudiant.compte_crou)}</span></div>
                    <div><span className="font-semibold">Date création :</span> {formatDate(selectedEtudiant.date_creation_carte)}</div>
                    <div><span className="font-semibold">Date expiration :</span> {formatDate(selectedEtudiant.date_expiration)}</div>
                    <div>
                      <span className="font-semibold">Statut validation :</span>
                      <span className={`ml-2 font-semibold ${selectedEtudiant.statut_validation === 'valide' ? 'text-green-600' : 'text-red-600'}`}>
                        {selectedEtudiant.statut_validation === 'valide' ? 'Validée' : 'Invalidée'}
                      </span>
                    </div>
                    <div><span className="font-semibold">Statut carte :</span> {selectedEtudiant.carte_statut === 'active' ? 'Active' : selectedEtudiant.carte_statut || 'Inconnu'}</div>
                  </div>
                ) : (
                  <div className="py-4 text-center"><p className="text-gray-500">Aucune carte associée à cet étudiant</p></div>
                )}
              </div>
              <div className="rounded-lg border p-3">
                <h4 className="mb-2 font-semibold text-orange-600">Carte d'identité</h4>
                {selectedEtudiant.carte_identite_url ? (
                  <div className="flex justify-center">
                    <img
                      src={selectedEtudiant.carte_identite_url}
                      alt="Carte d'identité"
                      className="max-h-80 rounded border object-contain shadow-md"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/400x300?text=Image+non+trouvee';
                      }}
                    />
                  </div>
                ) : (
                  <div className="rounded bg-gray-50 py-8 text-center">
                    <div className="mb-2 text-5xl">📄</div>
                    <p className="text-gray-500">Aucune carte d'identité fournie</p>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              {selectedEtudiant.id_carte && (
                selectedEtudiant.statut_validation === 'valide' ? (
                  <button
                    onClick={() => changerStatutValidation(selectedEtudiant.id_utilisateur, 'invalide', selectedEtudiant.nom, selectedEtudiant.prenom)}
                    className="rounded bg-red-500 px-4 py-2 text-white transition hover:bg-red-600"
                  >
                    Invalider la carte
                  </button>
                ) : (
                  <button
                    onClick={() => changerStatutValidation(selectedEtudiant.id_utilisateur, 'valide', selectedEtudiant.nom, selectedEtudiant.prenom)}
                    className="rounded bg-green-500 px-4 py-2 text-white transition hover:bg-green-600"
                  >
                    Valider la carte
                  </button>
                )
              )}
              <button
                onClick={() => setShowDetailsModal(false)}
                className="rounded bg-gray-500 px-4 py-2 text-white transition hover:bg-gray-600"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}